import { ListUserPortalsRequest } from '@eigtech/delegate-types'
import {
  GetJobSummariesRequest as BaseGetJobSummariesRequest,
  JobSummaryFilter,
  PageResponseProperties,
} from '@eigtech/summarizer-types'
import { queryClient } from '@/store'
import { OmitAuthHeaders, queryOptions, useQuery } from '@eigtech/ui-shared-api'
import { User, useAuth0 } from '@eigtech/ui-shared-auth'
import { summarizerBasePath } from '@eigtech/ui-shared-claims-lib'
import { DelegateAdminJobSummary, getJobLabelFromId } from '@eigtech/ui-shared-jobs'
import { normalizeFilters } from '@eigtech/ui-shared-summarizer'
import { get } from '../instance'
import { getActivePortalsQueryOptions } from '../portals'
import { jobsQueryKeys } from './constants'

export type AugmentedDelegateAdminJobSummary = DelegateAdminJobSummary & {
  jobLabel: string
}

const getAssignedJobs = async (request: ListUserPortalsRequest) => {
  const portals = await queryClient.fetchQuery(getActivePortalsQueryOptions(request))

  const jobIds = portals
    .filter((portal) => portal.entityType === 'job')
    .map((portal) => portal.entityId)

  const { jobs } = await getJobsSummaries({
    filter: normalizeFilters({
      filters: [
        {
          field: 'id',
          value: jobIds.join(','),
          operator: 'ANYOF',
        },
      ],
    }),
    page: 1,
    pageSize: 1000,
    sortAscending: false,
    sortBy: 'id',
  })

  return jobs.map(
    (job): AugmentedDelegateAdminJobSummary => ({
      ...job,
      jobLabel: getJobLabelFromId(job.id),
    })
  )
}

type GetJobSummariesRequest = Omit<OmitAuthHeaders<BaseGetJobSummariesRequest>, 'filter'> & {
  filter: (Omit<JobSummaryFilter, 'value'> & { value: string })[]
}

const getJobsSummaries = async ({ filter, ...params }: GetJobSummariesRequest) =>
  get<{ jobs: DelegateAdminJobSummary[] } & PageResponseProperties>(`${summarizerBasePath}/jobs`, {
    searchParams: { ...params, filter: encodeURI(JSON.stringify(filter)) },
  })

export function getAssignedJobsQueryOptions(user: User | undefined) {
  const auth0Id = user?.sub ?? ''
  const email = user?.email ?? ''

  return queryOptions({
    queryKey: jobsQueryKeys.list(),
    queryFn: () => getAssignedJobs({ auth0Id, email }),
    enabled: !!auth0Id && !!email,
  })
}

export function useGetAssignedJobs() {
  const { user } = useAuth0()

  return useQuery(getAssignedJobsQueryOptions(user))
}
